import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
// Using performance-optimized motion wrapper instead of framer-motion
import { motion, Variants, Easing, useScroll, useTransform, useSpring, useInView, AnimatePresence } from '@/lib/motion-wrapper';
import { Star, ChevronRight, CheckCircle, ArrowUp } from 'lucide-react';
import { ImageCarousel } from '@/components/ui/image-carousel';
import LazyImage from '@/components/ui/LazyImage';
import MarketplaceSection from '@/components/ui/MarketplaceSection';
import VideoTestimonial from '@/components/ui/VideoTestimonial';

// Product interface
interface Product {
  id: number;
  name: string;
  description: string;
  price: number;
  original_price: number;
  image_url: string;
  rating?: number;
  discount_percent?: number;
  category: string;
  weight: string;
}

// Carousel image interface
interface CarouselImage {
  image: string;
  link: string;
  title: string;
}

import { useCart } from '@/contexts/CartContext';
import AdminCarouselBanner from '@/components/ui/AdminCarouselBanner';

// Carousel images (for hero section)
const carouselImages: CarouselImage[] = [
  {
    image: "https://res.cloudinary.com/dlvxjnycr/image/upload/v1745910077/4_rrgvey.jpg",
    link: "/product/peri-peri-24",
    title: "Classic Roasted Makhana"
  },
  {
    image: "https://res.cloudinary.com/dlvxjnycr/image/upload/v1745910080/5_tdxi1b.jpg",
    link: "/product/masala-makhana",
    title: "Masala Makhana"
  },
  {
    image: "https://res.cloudinary.com/dlvxjnycr/image/upload/v1745910077/1_hgavbu.jpg",
    link: "/product/cheese-makhana",
    title: "Cheese Makhana"
  },
  {
    image: "https://res.cloudinary.com/dlvxjnycr/image/upload/v1745910076/3_klm4om.jpg",
    link: "/product/spicy-makhana",
    title: "Spicy Makhana"
  },
  {
    image: "https://res.cloudinary.com/dlvxjnycr/image/upload/v1745910077/4_rrgvey.jpg",
    link: "/product/peri-peri-24",
    title: "Classic Roasted Makhana"
  }
];

// Scroll to top component - simplified without animations
const ScrollToTop = () => {
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 300) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  
  return isVisible ? (
    <button
      onClick={scrollToTop}
      className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 md:bottom-8 md:right-8 z-50 p-2.5 sm:p-3 bg-brand-600 text-white rounded-full shadow-lg hover:bg-brand-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2"
      aria-label="Scroll to top"
    >
      <ArrowUp className="w-5 h-5 sm:w-6 sm:h-6" />
    </button>
  ) : null;
};

const Home = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [featuredProducts, setFeaturedProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const { addItem } = useCart();
  
  // Preload fallback products to show immediately
  const fallbackProducts: Product[] = React.useMemo(() => [
    {
      id: 1,
      name: "Premium Roasted Makhana",
      description: "Lightly salted and perfectly roasted makhana",
      price: 50,
      original_price: 70,
      image_url: "/placeholder-product.svg",
      category: "Roasted Makhana",
      weight: "50g",
      rating: 4.7,
      discount_percent: 29
    },
    {
      id: 2,
      name: "Spicy Peri Peri Makhana",
      description: "Tangy and spicy peri peri flavored makhana",
      price: 55,
      original_price: 75,
      image_url: "/placeholder-product.svg",
      category: "Flavored Makhana",
      weight: "50g",
      rating: 4.8,
      discount_percent: 27
    },
    {
      id: 3,
      name: "Sweet Honey Makhana",
      description: "Sweet and crunchy honey glazed makhana",
      price: 60,
      original_price: 80,
      image_url: "/placeholder-product.svg",
      category: "Sweet Makhana",
      weight: "50g",
      rating: 4.9,
      discount_percent: 25
    },
    {
      id: 4,
      name: "Classic Masala Makhana",
      description: "Traditional Indian masala flavored makhana",
      price: 48,
      original_price: 65,
      image_url: "/placeholder-product.svg",
      category: "Spicy Makhana",
      weight: "50g",
      rating: 4.8,
      discount_percent: 26
    }
  ], []);
  
  const fetchFeaturedProducts = React.useCallback(async () => {
    // Show fallback products immediately
    setFeaturedProducts(fallbackProducts);
    
    try {
      // Fetch fresh products in the background
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .not('name', 'ilike', '%Raw Makhana%')
        .limit(4);
      
      if (error) {
        console.error('Database error:', error);
        return; // Keep showing fallback products on error
      }
      
      if (data && data.length > 0) {
        const transformedProducts = data.map(product => {
          // Use the database price as the main price
          const price = Number(product.price) || 50;
          // Calculate discount (assuming no sale price in the database)
          const discount = 0; // No sale price in the database schema
            
          return {
            id: product.id,
            name: product.name || 'Makhana Product',
            description: product.description || 'Premium quality makhana',
            price: price, // No sale price in the database
            original_price: price,
            image_url: product.image_url || '/placeholder-product.svg',
            category: 'Makhana', // Default category since it's not in the database
            weight: '50g', // Default weight since it's not in the database
            rating: 4.5 + Math.random() * 0.5, // Random rating between 4.5 and 5.0
            discount_percent: discount
          };
        });
        
        // Only update if we have actual different products
        if (JSON.stringify(transformedProducts) !== JSON.stringify(fallbackProducts)) {
          setFeaturedProducts(transformedProducts);
        }
      }
    } catch (error) {
      console.error('Error in fetchFeaturedProducts:', error);
      // Keep showing the fallback products on error
    } finally {
      setLoading(false);
    }
  }, [fallbackProducts]);

  // Load products on component mount
  React.useEffect(() => {
    // Set a small timeout to prevent blocking the main thread
    const timer = setTimeout(() => {
      fetchFeaturedProducts();
    }, 0);
    
    return () => clearTimeout(timer);
  }, [fetchFeaturedProducts]);

  // Carousel interval reference
  const intervalRef = React.useRef<NodeJS.Timeout>();

  // Start the auto-slide interval
  const startInterval = React.useCallback(() => {
    intervalRef.current = setInterval(() => {
      if (!isHovered && carouselImages.length > 0) {
        setCurrentSlide(prev => (prev + 1) % carouselImages.length);
      }
    }, 5000); // 5 seconds
  }, [isHovered, carouselImages.length]);

  // Reset the auto-slide interval
  const resetInterval = React.useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    startInterval();
  }, [startInterval]);

  // Auto slide effect with pause on hover and cleanup
  useEffect(() => {
    let touchStartX = 0;
    let touchEndX = 0;

    const handleTouchStart = (e: TouchEvent) => {
      touchStartX = e.changedTouches[0].screenX;
    };

    const handleTouchEnd = (e: TouchEvent) => {
      touchEndX = e.changedTouches[0].screenX;
      handleSwipe();
    };

    const handleSwipe = () => {
      const swipeThreshold = 50; // Minimum swipe distance in pixels
      const difference = touchStartX - touchEndX;

      if (Math.abs(difference) > swipeThreshold) {
        if (difference > 0) {
          // Swipe left - next slide
          goToNextSlide();
        } else {
          // Swipe right - previous slide
          goToPreviousSlide();
        }
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        goToPreviousSlide();
      } else if (e.key === 'ArrowRight') {
        goToNextSlide();
      }
    };

    // Start the interval when component mounts
    startInterval();

    // Add keyboard event listeners
    window.addEventListener('keydown', handleKeyDown);

    // Add touch event listeners
    const carouselElement = document.querySelector('.carousel-container');
    if (carouselElement) {
      carouselElement.addEventListener('touchstart', handleTouchStart);
      carouselElement.addEventListener('touchend', handleTouchEnd);
    }

    // Cleanup function
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      window.removeEventListener('keydown', handleKeyDown);
      if (carouselElement) {
        carouselElement.removeEventListener('touchstart', handleTouchStart);
        carouselElement.removeEventListener('touchend', handleTouchEnd);
      }
    };
  }, [carouselImages.length, isHovered, startInterval]);

  // Navigate to next slide
  const goToNextSlide = React.useCallback(() => {
    setCurrentSlide(prev => (prev + 1) % carouselImages.length);
    resetInterval();
  }, [carouselImages.length, resetInterval]);

  // Navigate to previous slide
  const goToPreviousSlide = React.useCallback(() => {
    setCurrentSlide(prev => (prev - 1 + carouselImages.length) % carouselImages.length);
    resetInterval();
  }, [carouselImages.length, resetInterval]);



  const handleAddToCart = (product: Product) => {
    addItem({
      id: product.id,
      name: product.name,
      price: product.price,
      image_url: product.image_url,
      weight: parseInt(product.weight) || 50 // Convert weight string to number
    });
    toast({
      title: "Added to Cart!",
      description: `${product.name} has been added to your cart.`,
    });
  };

  const handleNewsletterSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (email) {
      toast({
        title: "Success!",
        description: "Thank you for subscribing to our newsletter.",
      });
      setEmail('');
    }
  };

  const renderStarRating = (rating: number) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;

    for (let i = 0; i < fullStars; i++) {
      stars.push(<Star key={`full-${i}`} className="w-4 h-4 fill-gold-500 text-gold-500" />);
    }

    if (hasHalfStar) {
      stars.push(
        <div key="half" className="relative w-4 h-4">
          <Star className="w-4 h-4 absolute text-gold-500" />
          <div className="absolute top-0 left-0 w-1/2 overflow-hidden">
            <Star className="w-4 h-4 fill-gold-500 text-gold-500" />
          </div>
        </div>
      );
    }

    const remainingStars = 5 - stars.length;
    for (let i = 0; i < remainingStars; i++) {
      stars.push(<Star key={`empty-${i}`} className="w-4 h-4 text-gold-300" />);
    }

    return (
      <div className="flex">
        {stars}
        <span className="ml-1 text-sm text-brand-700">{rating.toFixed(1)}</span>
      </div>
    );
  };

  // Animation variants (kept for compatibility, but won't animate due to wrapper)
  const staggerChildren: Variants = {};
  const childVariants: Variants = {};

  // Hero section image
  const heroImage = "https://res.cloudinary.com/dlvxjnycr/image/upload/v1745910077/4_rrgvey.jpg";
  const aboutImage = "https://res.cloudinary.com/dlvxjnycr/image/upload/v1745910080/5_tdxi1b.jpg";
  const healthImage = "https://res.cloudinary.com/dlvxjnycr/image/upload/v1745910077/1_hgavbu.jpg";

  return (
    <div className="relative overflow-x-hidden">
      {/* Scroll to top button */}
      <ScrollToTop />
      
      {/* Admin Carousel Banner - Dynamic from Database */}
      <AdminCarouselBanner />
      
      {/* Hero Section - Optimized without animations */}
      <section 
        className="relative py-12 sm:py-16 md:py-24 lg:py-32 bg-gradient-to-b from-cream-50 to-cream-200 overflow-hidden min-h-[85vh] sm:min-h-screen flex items-center"
      >
        {/* Static background blobs - no animation */}
        <div className="absolute top-0 -right-1/4 w-2/3 h-2/3 rounded-full bg-gold-300 mix-blend-multiply filter blur-[100px] opacity-30" />
        <div className="absolute bottom-0 -left-1/4 w-2/3 h-2/3 rounded-full bg-brand-500 mix-blend-multiply filter blur-[100px] opacity-30" />
        
        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 items-center">
            {/* Left Column - Text Content */}
            <div className="order-2 lg:order-1 px-4 sm:px-0">
              <div>
                <span className="px-3 sm:px-4 py-1 sm:py-1.5 bg-brand-100 text-brand-700 rounded-full text-xs sm:text-sm font-medium mb-4 sm:mb-6 inline-block shadow-sm">Premium Quality & Taste</span>
              </div>
              
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold mb-4 sm:mb-6 text-brand-800 leading-tight font-playfair">
                <span className="relative inline-block">
                  Premium <span className="text-brand-600">Makhana</span>
                  <span className="absolute bottom-0 sm:bottom-1 left-0 w-full h-1 sm:h-2 bg-gold-400 -z-10" />
                </span>
                <br /> 
                <span className="text-brand-700">Healthy Snacks</span>
              </h1>
              
              <p className="text-base sm:text-lg md:text-xl lg:text-2xl mb-6 sm:mb-10 text-brand-700 max-w-lg leading-relaxed">
                Discover our artisanal, nutrient-rich makhana snacks — where health meets extraordinary flavor in every bite.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 md:gap-6">
                <Link to="/products" className="w-full sm:w-auto">
                  <Button className="w-full sm:w-auto bg-brand-600 hover:bg-brand-700 text-white px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg shadow-lg hover:shadow-xl transition-colors duration-200 group">
                    <span>Explore Products</span>
                    <ChevronRight className="ml-2 h-4 w-4 sm:h-5 sm:w-5" />
                  </Button>
                </Link>
                <Link to="/about" className="w-full sm:w-auto">
                  <Button variant="outline" className="w-full sm:w-auto border-brand-600 text-brand-600 hover:bg-brand-600 hover:text-white px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg shadow-md hover:shadow-lg transition-colors duration-200">
                    Our Story
                  </Button>
                </Link>
              </div>
              
              <div className="mt-8 sm:mt-12">
                <ul className="flex flex-wrap items-center gap-3 sm:gap-6">
                  <li className="flex items-center text-brand-700 text-sm sm:text-base">
                    <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5 sm:mr-2 text-brand-600 flex-shrink-0" />
                    <span>Low Calorie</span>
                  </li>
                  <li className="flex items-center text-brand-700 text-sm sm:text-base">
                    <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5 sm:mr-2 text-brand-600 flex-shrink-0" />
                    <span>Gluten-Free</span>
                  </li>
                  <li className="flex items-center text-brand-700 text-sm sm:text-base">
                    <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5 sm:mr-2 text-brand-600 flex-shrink-0" />
                    <span>High Protein</span>
                  </li>
                </ul>
              </div>
            </div>
            
            {/* Right Column - Image/Carousel */}
            <div className="relative order-1 lg:order-2 px-4 sm:px-0">
              <div className="absolute -inset-2 rounded-2xl bg-gradient-to-r from-gold-400/20 to-brand-400/20 blur-xl -z-10" />
              <div className="bg-white/10 backdrop-blur-sm p-1.5 sm:p-2 rounded-2xl shadow-xl max-w-md mx-auto carousel-container">
                <div 
                  className="overflow-hidden rounded-xl relative"
                  onMouseEnter={() => setIsHovered(true)}
                  onMouseLeave={() => setIsHovered(false)}
                >
                  {carouselImages[currentSlide].link.startsWith('http') ? (
                    <a href={carouselImages[currentSlide].link} target="_blank" rel="noopener noreferrer">
                      <div className="relative group cursor-pointer">
                        <div className="w-full">
                      <LazyImage 
                        src={carouselImages[currentSlide].image} 
                        alt={carouselImages[currentSlide].title}
                        className="w-full h-auto rounded-xl transition-transform duration-200 group-hover:scale-105"
                        priority={true}
                        objectFit="contain"
                        height="400px"
                      />
                    </div>
                        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded-xl">
                          <div className="absolute bottom-4 left-4 right-4 text-white">
                            <h3 className="text-lg font-semibold">{carouselImages[currentSlide].title}</h3>
                            <p className="text-sm text-white/80">Click to view product</p>
                          </div>
                        </div>
                      </div>
                    </a>
                  ) : (
                    <Link to={carouselImages[currentSlide].link}>
                      <div className="relative group cursor-pointer">
                        <div className="w-full">
                      <LazyImage 
                        src={carouselImages[currentSlide].image} 
                        alt={carouselImages[currentSlide].title}
                        className="w-full h-auto rounded-xl transition-transform duration-200 group-hover:scale-105"
                        priority={true}
                        objectFit="contain"
                        height="400px"
                      />
                    </div>
                        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded-xl">
                          <div className="absolute bottom-4 left-4 right-4 text-white">
                            <h3 className="text-lg font-semibold">{carouselImages[currentSlide].title}</h3>
                            <p className="text-sm text-white/80">Click to view product</p>
                          </div>
                        </div>
                      </div>
                    </Link>
                  )}

                  {/* Navigation Arrows */}
                  <button 
                    onClick={() => {
                      setCurrentSlide(prev => (prev - 1 + carouselImages.length) % carouselImages.length);
                      resetInterval();
                    }}
                    className={`absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-brand-800 p-2 rounded-full shadow-md transition-opacity duration-200 ${
                      isHovered ? 'opacity-100' : 'opacity-0'
                    }`}
                    aria-label="Previous image"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>
                  <button 
                    onClick={() => {
                      setCurrentSlide(prev => (prev + 1) % carouselImages.length);
                      resetInterval();
                    }}
                    className={`absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-brand-800 p-2 rounded-full shadow-md transition-opacity duration-200 ${
                      isHovered ? 'opacity-100' : 'opacity-0'
                    }`}
                    aria-label="Next image"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>

                  {/* Dot Indicators */}
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2">
                    {carouselImages.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => {
                          setCurrentSlide(index);
                          resetInterval();
                        }}
                        className={`w-2 h-2 rounded-full transition-transform duration-150 ${
                          index === currentSlide 
                            ? 'bg-white w-4' 
                            : 'bg-white/60 hover:bg-white/80'
                        }`}
                        aria-label={`Go to slide ${index + 1}`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Benefits Bar */}
          <div className="mt-12 sm:mt-16 md:mt-24 grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-6 md:gap-10 px-4 sm:px-0">
            <div className="bg-white/80 backdrop-blur rounded-lg px-3 sm:px-4 md:px-6 py-3 sm:py-4 shadow-md hover:shadow-lg transition-shadow">
              <div className="text-brand-600 mb-1 sm:mb-2 text-base sm:text-lg md:text-xl font-bold">Gluten-Free</div>
              <p className="text-brand-700 text-xs sm:text-sm">Safe for celiac & gluten-sensitive individuals</p>
            </div>
            
            <div className="bg-white/80 backdrop-blur rounded-lg px-3 sm:px-4 md:px-6 py-3 sm:py-4 shadow-md hover:shadow-lg transition-shadow">
              <div className="text-brand-600 mb-1 sm:mb-2 text-base sm:text-lg md:text-xl font-bold">Low Calorie</div>
              <p className="text-brand-700 text-xs sm:text-sm">Perfect for weight management & mindful eating</p>
            </div>
            
            <div className="bg-white/80 backdrop-blur rounded-lg px-3 sm:px-4 md:px-6 py-3 sm:py-4 shadow-md hover:shadow-lg transition-shadow">
              <div className="text-brand-600 mb-1 sm:mb-2 text-base sm:text-lg md:text-xl font-bold">High Protein</div>
              <p className="text-brand-700 text-xs sm:text-sm">Plant-based protein for sustained energy</p>
            </div>
            
            <div className="bg-white/80 backdrop-blur rounded-lg px-3 sm:px-4 md:px-6 py-3 sm:py-4 shadow-md hover:shadow-lg transition-shadow">
              <div className="text-brand-600 mb-1 sm:mb-2 text-base sm:text-lg md:text-xl font-bold">No Additives</div>
              <p className="text-brand-700 text-xs sm:text-sm">Free from artificial colors & preservatives</p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Products Grid */}
      <section className="py-12 sm:py-16 md:py-20 bg-cream-200">
        <div className="container-custom px-4 sm:px-6">
          <div className="text-center mb-8 sm:mb-12">
            <span className="px-3 sm:px-4 py-1 sm:py-1.5 bg-brand-100 text-brand-700 rounded-full text-xs sm:text-sm font-medium mb-3 sm:mb-4 inline-block">Our Products</span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4 font-playfair">Featured Products</h2>
            <div className="w-16 sm:w-20 h-1 bg-gold-500 mx-auto mb-4 sm:mb-6"></div>
            <p className="mt-3 sm:mt-4 text-sm sm:text-base md:text-lg text-brand-700 max-w-2xl mx-auto px-4">
              Discover our premium makhana varieties, crafted with care and premium ingredients.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {loading ? (
              // Loading skeleton
              Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className="bg-white rounded-lg shadow-md overflow-hidden animate-pulse">
                  <div className="h-64 bg-gray-200"></div>
                  <div className="p-4">
                    <div className="h-4 bg-gray-200 rounded mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded mb-3 w-2/3"></div>
                    <div className="h-4 bg-gray-200 rounded mb-3 w-1/2"></div>
                    <div className="h-8 bg-gray-200 rounded"></div>
                  </div>
                </div>
              ))
            ) : (
              featuredProducts.map((product) => {
              const discountPercent = Math.round(((product.original_price - product.price) / product.original_price) * 100);
              
              return (
                <div 
                  key={product.id}
                  className="group bg-white rounded-lg shadow-md overflow-hidden flex flex-col h-full transition-transform duration-150 hover:shadow-xl hover:-translate-y-1"
                >
                  <Link to={`/product/${product.id}`} className="block h-64 overflow-hidden">
                    <div className="relative h-full w-full">
                      <LazyImage 
                        src={product.image_url} 
                        alt={product.name} 
                        className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105" 
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-150 flex items-end p-4">
                        <div className="text-white">
                          <h3 className="text-lg font-semibold">{product.name}</h3>
                          <p className="text-sm opacity-90">{product.weight}</p>
                        </div>
                      </div>
                    </div>
                  </Link>
                  <div className="p-4 flex-grow flex flex-col">
                    <h3 className="text-base font-medium text-brand-800 mb-1 line-clamp-1">{product.name}</h3>
                    <p className="text-sm text-brand-600 mb-3">{product.category}</p>
                    <div className="mt-auto">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-lg font-bold text-brand-900">₹{product.price}</span>
                        {product.original_price > product.price && (
                          <>
                            <span className="text-sm text-gray-400 line-through">₹{product.original_price}</span>
                            <span className="text-xs bg-green-100 text-green-800 px-1.5 py-0.5 rounded ml-auto">
                              {discountPercent}% OFF
                            </span>
                          </>
                        )}
                      </div>
                      <Button 
                        onClick={() => handleAddToCart(product)}
                        className="w-full bg-brand-600 hover:bg-brand-700 text-sm py-2 h-auto transition-transform duration-150 hover:shadow-md"
                      >
                        Add to Cart
                      </Button>
                    </div>
                  </div>
                </div>
              );
              })
            )}
          </div>
          
          <div className="text-center mt-8 sm:mt-12 px-4">
            <Link to="/products" className="inline-block w-full sm:w-auto">
              <Button className="w-full sm:w-auto bg-brand-600 hover:bg-brand-700 px-6 sm:px-8 py-3 sm:py-4 text-sm sm:text-base">
                View All Products
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* About Snippet with Animation - Removed Team Section */}
      <section className="py-16 sm:py-20 md:py-24 lg:py-32 bg-white relative overflow-hidden">
        <motion.div 
          className="absolute top-40 right-[20%] w-96 h-96 rounded-full bg-cream-300 mix-blend-multiply filter blur-[100px] opacity-30"
          animate={{ 
            x: [0, 30, -20, 0],
            y: [0, -40, 20, 0],
            scale: [1, 1.1, 0.9, 1],
          }}
          transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
        />
        
        <div className="container-custom px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-12 sm:gap-16 items-center mt-0">
            {/* About Company section */}
            <motion.div 
              className="text-center mb-8 sm:mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="px-3 sm:px-4 py-1 sm:py-1.5 bg-brand-100 text-brand-700 rounded-full text-xs sm:text-sm font-medium mb-3 sm:mb-4 inline-block">About Us</span>
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-3 sm:mb-4 font-playfair">About Natural Puff</h2>
              <div className="w-20 sm:w-24 h-1 bg-gold-500 mx-auto mb-4 sm:mb-6"></div>
              <p className="mt-3 sm:mt-4 text-base sm:text-lg md:text-xl text-brand-700 max-w-2xl mx-auto px-4">
                Natural Puff was born from the desire to reimagine traditional makhana (fox nuts) 
                into premium, flavorful snacks that don't compromise on taste or nutrition.
              </p>
              <div className="mt-6 sm:mt-8">
                <Link to="/about" className="inline-block w-full sm:w-auto px-4">
                  <Button className="w-full sm:w-auto bg-brand-600 hover:bg-brand-700 text-white px-6 sm:px-8 py-3 sm:py-4">
                    Learn More About Us
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Health Benefits Section */}
      <section className="py-12 sm:py-16 md:py-20 lg:py-28 bg-brand-700 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-pattern opacity-10"></div>
        <motion.div 
          className="absolute top-1/4 right-1/4 w-96 h-96 rounded-full bg-brand-500 mix-blend-soft-light filter blur-[80px] opacity-40"
          animate={{ 
            x: [0, 50, -30, 0],
            y: [0, -30, 50, 0],
          }}
          transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" as Easing }}
        />
        <motion.div 
          className="absolute bottom-0 left-1/4 w-80 h-80 rounded-full bg-gold-500 mix-blend-soft-light filter blur-[80px] opacity-40"
          animate={{ 
            x: [0, -40, 30, 0],
            y: [0, 50, -30, 0],
          }}
          transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" as Easing }}
        />
        
        <div className="container-custom relative z-10 px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="order-2 lg:order-1"
            >
              <motion.span 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2, duration: 0.6 }}
                className="px-3 sm:px-4 py-1 sm:py-1.5 bg-white/20 text-white rounded-full text-xs sm:text-sm font-medium mb-4 sm:mb-6 inline-block backdrop-blur-sm"
              >
                Health & Wellness
              </motion.span>
              
              <motion.h2 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3, duration: 0.6 }}
                className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 font-playfair"
              >
                Supercharge Your Health
              </motion.h2>
              
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4, duration: 0.6 }}
                className="w-16 sm:w-20 h-1 bg-gold-500 mb-6 sm:mb-8"
              />
              
              <motion.p 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5, duration: 0.6 }}
                className="text-sm sm:text-base md:text-lg lg:text-xl mb-6 sm:mb-8 leading-relaxed"
              >
                Makhana, also known as fox nuts or lotus seeds, are packed with essential nutrients 
                that provide remarkable benefits for your overall well-being and vitality.
              </motion.p>
              
              <motion.ul 
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={staggerChildren}
                className="space-y-4 sm:space-y-5 mb-8 sm:mb-10"
              >
                <motion.li variants={childVariants} className="flex items-start">
                  <CheckCircle className="h-5 w-5 sm:h-6 sm:w-6 mr-2 sm:mr-3 text-gold-300 flex-shrink-0" />
                  <div>
                    <span className="text-base sm:text-lg font-medium">Rich in Antioxidants</span>
                    <p className="text-white/80 mt-1 text-sm sm:text-base">Helps neutralize free radicals and reduce oxidative stress</p>
                  </div>
                </motion.li>
                
                <motion.li variants={childVariants} className="flex items-start">
                  <CheckCircle className="h-5 w-5 sm:h-6 sm:w-6 mr-2 sm:mr-3 text-gold-300 flex-shrink-0" />
                  <div>
                    <span className="text-base sm:text-lg font-medium">Low Glycemic Index</span>
                    <p className="text-white/80 mt-1 text-sm sm:text-base">Perfect for managing blood sugar levels and sustained energy</p>
                  </div>
                </motion.li>
                
                <motion.li variants={childVariants} className="flex items-start">
                  <CheckCircle className="h-5 w-5 sm:h-6 sm:w-6 mr-2 sm:mr-3 text-gold-300 flex-shrink-0" />
                  <div>
                    <span className="text-base sm:text-lg font-medium">Heart Health Support</span>
                    <p className="text-white/80 mt-1 text-sm sm:text-base">Promotes cardiovascular wellness through plant compounds</p>
                  </div>
                </motion.li>
                
                <motion.li variants={childVariants} className="flex items-start">
                  <CheckCircle className="h-5 w-5 sm:h-6 sm:w-6 mr-2 sm:mr-3 text-gold-300 flex-shrink-0" />
                  <div>
                    <span className="text-base sm:text-lg font-medium">Weight Management</span>
                    <p className="text-white/80 mt-1 text-sm sm:text-base">High fiber, low calorie profile helps with satiety and digestion</p>
                  </div>
                </motion.li>
              </motion.ul>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.9, duration: 0.6 }}
              >
                <Link to="/health-benefits" className="inline-block w-full sm:w-auto">
                  <Button className="w-full sm:w-auto bg-gold-500 hover:bg-gold-600 text-brand-800 px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg shadow-lg hover:shadow-xl transition-transform duration-150 group">
                    <span>Explore Health Benefits</span>
                    <ChevronRight className="ml-2 h-4 w-4 sm:h-5 sm:w-5 transition-transform group-hover:translate-x-1" />
                  </Button>
                </Link>
              </motion.div>
            </motion.div>
            
            <motion.div 
              className="relative order-1 lg:order-2"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="absolute -inset-4 rounded-3xl bg-gradient-to-r from-gold-400/20 to-brand-400/20 blur-2xl -z-10" />
              <div className="bg-white/10 backdrop-blur-sm p-2 sm:p-3 md:p-4 rounded-3xl shadow-2xl">
                <div className="overflow-hidden rounded-2xl relative">
                  <img 
                    src={healthImage} 
                    alt="Health Benefits of Makhana" 
                    className="w-full h-auto rounded-2xl"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                </div>
              </div>
              
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4, duration: 0.6 }}
                className="absolute top-3 right-3 sm:top-4 sm:right-4 md:top-6 md:right-6 bg-gold-500/90 backdrop-blur-sm px-3 py-1.5 sm:px-4 sm:py-2 rounded-full shadow-lg"
              >
                <span className="text-brand-800 font-medium text-sm sm:text-base">Nutrient-Rich</span>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>


    {/* Why Choose Us Section */}
    <section className="py-12 sm:py-16 md:py-20 lg:py-24 bg-cream-50 relative overflow-hidden">
      <motion.div 
        className="absolute top-20 left-[10%] w-96 h-96 rounded-full bg-brand-200 mix-blend-multiply filter blur-[100px] opacity-30"
        animate={{ 
          x: [0, 30, -20, 0],
          y: [0, -40, 20, 0],
        }}
        transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" as Easing }}
      />
      <div className="container-custom px-4 sm:px-6">
        <motion.div 
          className="text-center mb-10 sm:mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="px-3 sm:px-4 py-1 sm:py-1.5 bg-brand-100 text-brand-700 rounded-full text-xs sm:text-sm font-medium mb-3 sm:mb-4 inline-block">Why Natural Puff</span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-3 sm:mb-4 font-playfair">Why Choose Us</h2>
          <div className="w-20 sm:w-24 h-1 bg-gold-500 mx-auto mb-4 sm:mb-6"></div>
        </motion.div>

        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
          {[
            {
              icon: "🌾",
              title: "Premium Quality",
              description: "We source the finest quality makhana from trusted farmers in Bihar, ensuring premium grade lotus seeds for our products."
            },
            {
              icon: "🔬",
              title: "Quality Control",
              description: "Rigorous quality checks at every stage of production ensure that you receive only the best snacks."
            },
            {
              icon: "🌿",
              title: "Natural Ingredients",
              description: "We use only natural ingredients and authentic spices, with no artificial preservatives or additives."
            },
            {
              icon: "✨",
              title: "Innovative Flavors",
              description: "Our unique flavor combinations are crafted to delight your taste buds while maintaining nutritional value."
            },
            {
              icon: "📦",
              title: "Secure Packaging",
              description: "Special moisture-resistant packaging ensures your makhana stays fresh and crispy for longer."
            },
            {
              icon: "💝",
              title: "Customer First",
              description: "We value our customers and are committed to providing the best shopping experience and support."
            }
          ].map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="bg-white rounded-xl p-4 sm:p-6 shadow-lg hover:shadow-xl transition-transform duration-150"
            >
              <div className="text-3xl sm:text-4xl mb-3 sm:mb-4">{feature.icon}</div>
              <h3 className="text-lg sm:text-xl font-bold text-brand-800 mb-2 sm:mb-3">{feature.title}</h3>
              <p className="text-sm sm:text-base text-brand-700">{feature.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    {/* Sustainability Commitment Section */}
    <section className="py-12 sm:py-16 md:py-20 lg:py-24 bg-brand-50 relative overflow-hidden">
      <motion.div 
        className="absolute bottom-20 right-[10%] w-96 h-96 rounded-full bg-green-200 mix-blend-multiply filter blur-[100px] opacity-30"
        animate={{ 
          x: [0, -30, 20, 0],
          y: [0, 40, -20, 0],
        }}
        transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" as Easing }}
      />
      <div className="container-custom px-4 sm:px-6">
        <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <motion.span 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="px-3 sm:px-4 py-1 sm:py-1.5 bg-green-100 text-green-700 rounded-full text-xs sm:text-sm font-medium mb-4 sm:mb-6 inline-block"
            >
              Our Commitment
            </motion.span>
            
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 font-playfair text-brand-800"
            >
              Sustainable & Responsible
            </motion.h2>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="w-16 sm:w-20 h-1 bg-green-500 mb-6 sm:mb-8"
            />
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="text-sm sm:text-base md:text-lg lg:text-xl mb-6 sm:mb-8 text-brand-700 leading-relaxed"
            >
              We're committed to sustainable practices throughout our supply chain, from farming to packaging. Our mission is to provide healthy snacks while protecting our environment.
            </motion.p>
            
            <motion.ul 
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerChildren}
              className="space-y-5 mb-10"
            >
              {[
                {
                  title: "Eco-Friendly Packaging",
                  description: "Using recyclable materials to reduce environmental impact"
                },
                {
                  title: "Farmer Partnership",
                  description: "Supporting local farmers with fair trade practices"
                },
                {
                  title: "Zero Waste Policy",
                  description: "Minimizing waste in our production process"
                },
                {
                  title: "Water Conservation",
                  description: "Implementing water-saving techniques in farming"
                }
              ].map((item, index) => (
                <motion.li key={index} variants={childVariants} className="flex items-start">
                  <CheckCircle className="h-5 w-5 sm:h-6 sm:w-6 mr-2 sm:mr-3 text-green-600 flex-shrink-0" />
                  <div>
                    <span className="text-base sm:text-lg font-medium text-brand-800">{item.title}</span>
                    <p className="text-sm sm:text-base text-brand-700 mt-1">{item.description}</p>
                  </div>
                </motion.li>
              ))}
            </motion.ul>
          </motion.div>
          
          <motion.div
            className="relative order-1 lg:order-2"
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="absolute -inset-4 rounded-3xl bg-gradient-to-r from-green-400/20 to-brand-400/20 blur-2xl -z-10" />
            <div className="grid grid-cols-2 gap-3 sm:gap-4 md:gap-6">
              <div className="bg-white rounded-lg p-3 sm:p-4 md:p-6 shadow-lg">
                <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-blue-600 mb-1 sm:mb-2">24/7</h3>
                <p className="text-sm sm:text-base md:text-lg font-medium text-brand-800">Quality Monitoring</p>
                <p className="text-xs sm:text-sm text-brand-700">Continuous quality assurance process</p>
              </div>
              <div className="bg-white rounded-lg p-3 sm:p-4 md:p-6 shadow-lg">
                <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-blue-600 mb-1 sm:mb-2">100%</h3>
                <p className="text-sm sm:text-base md:text-lg font-medium text-brand-800">Automated</p>
                <p className="text-xs sm:text-sm text-brand-700">Processing & packaging systems</p>
              </div>
              <div className="bg-white rounded-lg p-3 sm:p-4 md:p-6 shadow-lg">
                <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-blue-600 mb-1 sm:mb-2">5+</h3>
                <p className="text-sm sm:text-base md:text-lg font-medium text-brand-800">Quality Checks</p>
                <p className="text-xs sm:text-sm text-brand-700">At every production stage</p>
              </div>
              <div className="bg-white rounded-lg p-3 sm:p-4 md:p-6 shadow-lg">
                <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-blue-600 mb-1 sm:mb-2">99%</h3>
                <p className="text-sm sm:text-base md:text-lg font-medium text-brand-800">Accuracy</p>
                <p className="text-xs sm:text-sm text-brand-700">In quality classification</p>
              </div>
            </div>
            <div className="bg-blue-50/50 rounded-lg p-4 sm:p-6 shadow-lg mt-4 sm:mt-6">
              <h3 className="text-lg sm:text-xl font-bold text-brand-800 mb-2">Innovation Hub</h3>
              <p className="text-sm sm:text-base text-brand-700">Our state-of-the-art facility combines traditional expertise with modern technology to deliver premium quality makhana products.</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>

    {/* Video Testimonial Section */}
    <VideoTestimonial />

    {/* Available on Marketplaces Section */}
    <MarketplaceSection />

    {/* Newsletter Signup with Animation */}
    <section className="py-12 sm:py-16 md:py-20 lg:py-28 bg-cream-100 relative overflow-hidden">
      <motion.div 
        className="absolute top-20 right-[10%] w-96 h-96 rounded-full bg-gold-400 mix-blend-multiply filter blur-3xl opacity-20"
        animate={{ 
          x: [0, 30, -20, 0],
          y: [0, -40, 20, 0],
        }}
        transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" as Easing }}
      />
      <motion.div 
        className="absolute bottom-20 left-[10%] w-96 h-96 rounded-full bg-brand-500 mix-blend-multiply filter blur-3xl opacity-20"
        animate={{ 
          x: [0, -30, 20, 0],
          y: [0, 40, -20, 0],
        }}
        transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" as Easing }}
      />
      
      <div className="container-custom relative z-10 px-4 sm:px-6">
        <motion.div 
          className="max-w-3xl mx-auto text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="px-3 sm:px-4 py-1 sm:py-1.5 bg-brand-100 text-brand-700 rounded-full text-xs sm:text-sm font-medium mb-4 sm:mb-6 inline-block">Stay Updated</span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-3 sm:mb-4 font-playfair">Join Our Newsletter</h2>
          <div className="w-20 sm:w-24 h-1 bg-gold-500 mx-auto mb-6 sm:mb-8"></div>
          <p className="text-sm sm:text-base md:text-lg lg:text-xl text-brand-700 mb-8 sm:mb-10 leading-relaxed px-4">
            Subscribe to receive updates on new products, special promotions, and healthy recipe ideas with makhana.
          </p>
          <form onSubmit={handleNewsletterSubmit} className="max-w-md mx-auto">
            <div className="flex flex-col sm:flex-row gap-3">
              <Input 
                type="email" 
                placeholder="Your email address" 
                className="flex-grow text-base sm:text-lg py-4 sm:py-6 shadow-md"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <Button 
                type="submit" 
                className="bg-brand-600 hover:bg-brand-700 text-white whitespace-nowrap px-6 sm:px-8 py-4 sm:py-6 shadow-lg hover:shadow-xl transition-transform duration-150 transform hover:scale-105"
              >
                Subscribe Now
              </Button>
            </div>
            <p className="text-xs sm:text-sm text-brand-700 mt-3 sm:mt-4 px-4">
              We respect your privacy and will never share your information.
            </p>
          </form>
        </motion.div>
      </div>
    </section>
    
    {/* Scroll to top button */}
    <ScrollToTop />
  </div>
  );
};

export default Home;
